import React from 'react';
import type { SchemeGrantRow } from '../../types/dashboard.types';

interface SectionTableProps {
  title: string;
  data: SchemeGrantRow[];
  color?: string;
}

const SectionTable: React.FC<SectionTableProps> = ({ title, data, color = 'blue' }) => {
  const headerColors: Record<string, string> = {
    blue: 'bg-blue-600 text-white',
    green: 'bg-green-600 text-white',
    orange: 'bg-orange-500 text-white',
    purple: 'bg-purple-600 text-white',
  };

  const totals = data.reduce(
    (acc, r) => ({
      total_nos: acc.total_nos + r.data.total.nos,
      total_cost: acc.total_cost + r.data.total.cost,
      roads_nos: acc.roads_nos + r.data.roads.nos,
      roads_length: acc.roads_length + r.data.roads.length,
      roads_cost: acc.roads_cost + r.data.roads.cost,
      bridges_nos: acc.bridges_nos + r.data.bridges.nos,
      bridges_cost: acc.bridges_cost + r.data.bridges.cost,
      other_nos: acc.other_nos + r.data.other_works.nos,
      other_cost: acc.other_cost + r.data.other_works.cost,
    }),
    { total_nos: 0, total_cost: 0, roads_nos: 0, roads_length: 0, roads_cost: 0, bridges_nos: 0, bridges_cost: 0, other_nos: 0, other_cost: 0 },
  );

  return (
    <div className="mb-6">
      <div className={`${headerColors[color] || headerColors.blue} px-4 py-2 rounded-t-lg`}>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <div className="overflow-x-auto border border-t-0 border-gray-200 rounded-b-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th rowSpan={2} className="px-3 py-2 text-left font-medium text-gray-700 border-b border-r">Scheme</th>
              <th rowSpan={2} className="px-3 py-2 text-left font-medium text-gray-700 border-b border-r">Grant</th>
              <th colSpan={2} className="px-3 py-1 text-center font-medium text-gray-700 border-b border-r">Total</th>
              <th colSpan={3} className="px-3 py-1 text-center font-medium text-gray-700 border-b border-r">Roads</th>
              <th colSpan={2} className="px-3 py-1 text-center font-medium text-gray-700 border-b border-r">Bridges</th>
              <th colSpan={2} className="px-3 py-1 text-center font-medium text-gray-700 border-b">Other Works</th>
            </tr>
            <tr className="text-xs text-gray-500">
              <th className="px-2 py-1 border-b border-r text-right">No's</th>
              <th className="px-2 py-1 border-b border-r text-right">Cost</th>
              <th className="px-2 py-1 border-b border-r text-right">No's</th>
              <th className="px-2 py-1 border-b border-r text-right">Length</th>
              <th className="px-2 py-1 border-b border-r text-right">Cost</th>
              <th className="px-2 py-1 border-b border-r text-right">No's</th>
              <th className="px-2 py-1 border-b border-r text-right">Cost</th>
              <th className="px-2 py-1 border-b border-r text-right">No's</th>
              <th className="px-2 py-1 border-b text-right">Cost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-6 text-center text-gray-400">No data available</td></tr>
            )}
            {data.map((row, i) => (
              <tr key={`${row.scheme_type}-${row.grant_type}-${i}`} className="hover:bg-gray-50">
                <td className="px-3 py-2 border-r font-medium">{row.scheme_type}</td>
                <td className="px-3 py-2 border-r">{row.grant_type}</td>
                <td className="px-2 py-2 border-r text-right">{row.data.total.nos}</td>
                <td className="px-2 py-2 border-r text-right">{row.data.total.cost.toFixed(2)}</td>
                <td className="px-2 py-2 border-r text-right">{row.data.roads.nos}</td>
                <td className="px-2 py-2 border-r text-right">{row.data.roads.length.toFixed(2)}</td>
                <td className="px-2 py-2 border-r text-right">{row.data.roads.cost.toFixed(2)}</td>
                <td className="px-2 py-2 border-r text-right">{row.data.bridges.nos}</td>
                <td className="px-2 py-2 border-r text-right">{row.data.bridges.cost.toFixed(2)}</td>
                <td className="px-2 py-2 border-r text-right">{row.data.other_works.nos}</td>
                <td className="px-2 py-2 text-right">{row.data.other_works.cost.toFixed(2)}</td>
              </tr>
            ))}
            {data.length > 0 && (
              <tr className="bg-gray-100 font-semibold">
                <td className="px-3 py-2 border-r" colSpan={2}>Total</td>
                <td className="px-2 py-2 border-r text-right">{totals.total_nos}</td>
                <td className="px-2 py-2 border-r text-right">{totals.total_cost.toFixed(2)}</td>
                <td className="px-2 py-2 border-r text-right">{totals.roads_nos}</td>
                <td className="px-2 py-2 border-r text-right">{totals.roads_length.toFixed(2)}</td>
                <td className="px-2 py-2 border-r text-right">{totals.roads_cost.toFixed(2)}</td>
                <td className="px-2 py-2 border-r text-right">{totals.bridges_nos}</td>
                <td className="px-2 py-2 border-r text-right">{totals.bridges_cost.toFixed(2)}</td>
                <td className="px-2 py-2 border-r text-right">{totals.other_nos}</td>
                <td className="px-2 py-2 text-right">{totals.other_cost.toFixed(2)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SectionTable;
